// /contacts/$contactId — ficha del contacto.
//
// Misma información que antes, ordenada como el modal de oportunidad:
// cabecera con acciones, columna izquierda (identidad y notas) y columna
// principal (actividad, oportunidades y tareas). El correo se escribe en
// un modal, no inline.

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState, type FormEvent } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import {
  ArrowLeft,
  ArrowRightLeft,
  Bot,
  Building2,
  CheckCircle2,
  Loader2,
  Mail,
  MessageCircle,
  MoreHorizontal,
  Pencil,
  Phone,
  Plus,
  Save,
  ShieldAlert,
  StickyNote,
  Trash2,
  TrendingUp,
  X as XIcon,
  type LucideIcon,
} from 'lucide-react';
import { Avatar } from '~/components/Avatar/Avatar';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import { EmailComposer } from '~/components/EmailComposer/EmailComposer';
import { SentEmailDialog, ViewEmailButton } from '~/components/SentEmailDialog/SentEmailDialog';
import { ContactConversations } from '~/components/LeadConversations/LeadConversations';
import { TagList } from '~/components/Tags/TagList';
import { Tooltip } from '~/components/Tooltip/Tooltip';
import { TaskFormModal, TASK_TYPE_LABEL } from '~/components/TaskFormModal/TaskFormModal';
import {
  useContact,
  useContactLeads,
  useContactActivities,
  useUpdateContact,
  useDeleteContact,
} from '~/lib/contacts-queries';
import { useContactTasks, useToggleTaskDone, useDeleteTask, parseTags } from '~/lib/queries';
import type { Activity, ActivityType, Contact, Task } from '~/lib/types';
import { currentUser } from '~/lib/pb';
import { can } from '~/lib/permissions';
import { formatMoney, leadCurrency } from '~/lib/capabilities';
import { personDisplayName } from '~/lib/display-name';
import { prettyPhone, phoneToTelLink } from '~/lib/phone';
import { useTenantContext } from '~/lib/tenantContext';
import styles from './_authed.contacts_.$contactId.module.css';

export const Route = createFileRoute('/_authed/contacts_/$contactId')({
  component: ContactDetailPage,
});

const ACTIVITY_META: Record<
  ActivityType,
  { icon: LucideIcon; label: string; tone: 'primary' | 'teal' | 'green' | 'amber' | 'neutral' }
> = {
  email_sent: { icon: Mail, label: 'Email enviado', tone: 'primary' },
  email_received: { icon: Mail, label: 'Email recibido', tone: 'primary' },
  email_opened: { icon: Mail, label: 'Email abierto', tone: 'teal' },
  email_clicked: { icon: Mail, label: 'Email clicado', tone: 'teal' },
  whatsapp_in: { icon: MessageCircle, label: 'WhatsApp recibido', tone: 'green' },
  whatsapp_out: { icon: MessageCircle, label: 'WhatsApp enviado', tone: 'green' },
  call: { icon: Phone, label: 'Llamada', tone: 'amber' },
  note: { icon: StickyNote, label: 'Nota', tone: 'neutral' },
  task: { icon: CheckCircle2, label: 'Tarea', tone: 'neutral' },
  chatbot_session: { icon: Bot, label: 'Chatbot', tone: 'green' },
  stage_change: { icon: ArrowRightLeft, label: 'Cambio de etapa', tone: 'neutral' },
  value_change: { icon: TrendingUp, label: 'Valor actualizado', tone: 'neutral' },
};

function contactDisplayName(c: Contact | undefined): string {
  if (!c) return '—';
  return personDisplayName(c) || (c.phone ? prettyPhone(c.phone) : null) || 'Sin nombre';
}

function formatDate(iso: string | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return new Intl.DateTimeFormat('es-CL', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d);
}

function EmailAction({ hasEmail, onOpen }: { hasEmail: boolean; onOpen: () => void }) {
  const button = (
    <button
      type="button"
      className={`btn btn--primary btn--sm ${styles.emailBtn}`}
      onClick={onOpen}
      disabled={!hasEmail}
    >
      <Mail size={14} aria-hidden /> Enviar email
    </button>
  );
  if (hasEmail) return button;
  return (
    <Tooltip content="Este contacto no tiene correo">
      <span className={styles.tooltipWrap} tabIndex={0}>
        {button}
      </span>
    </Tooltip>
  );
}

function ContactDetailPage() {
  const { contactId } = Route.useParams();
  const { selectedTenant } = useTenantContext();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const deleteMut = useDeleteContact();
  const { data: contact, isLoading, error } = useContact(contactId);
  const { data: leads } = useContactLeads(contactId);
  const { data: activities } = useContactActivities(contactId);
  const [editing, setEditing] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [creatingTask, setCreatingTask] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setEditing(false);
    setComposeOpen(false);
    setCreatingTask(false);
    setActionError(null);
  }, [contactId]);

  if (isLoading) {
    return <div className={styles.empty}>Cargando contacto…</div>;
  }

  if (error || !contact) {
    return (
      <div className={styles.notFound}>
        <ShieldAlert size={32} aria-hidden style={{ marginBottom: 12 }} />
        <p>No se encontró el contacto o no tienes acceso.</p>
        <Link to="/contacts" className={styles.backLink}>
          <ArrowLeft size={14} aria-hidden /> Volver a la lista
        </Link>
      </div>
    );
  }

  const name = contactDisplayName(contact);
  const subtitle = [contact.position, contact.company].filter(Boolean).join(' · ');
  const canDelete = can(currentUser()?.role, 'deleteRecords');
  const leadOptions = (leads ?? []).map((l) => ({
    id: l.id,
    title: l.title || '(sin título)',
  }));

  function goBack() {
    if (window.history.length > 1) navigate({ to: '..' });
    else navigate({ to: '/contacts' });
  }

  async function handleDelete() {
    const ok = await confirm({
      title: '¿Borrar este contacto?',
      description:
        'Esto NO borra las oportunidades asociadas; ellas quedarán sin contacto. Acción irreversible.',
      confirmLabel: 'Borrar contacto',
      tone: 'danger',
    });
    if (!ok) return;
    setActionError(null);
    try {
      await deleteMut.mutateAsync({ contactId: contact!.id });
      navigate({ to: '/contacts' });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : 'No pudimos borrar.');
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <button type="button" className={styles.back} onClick={goBack}>
          <ArrowLeft size={14} aria-hidden /> Volver
        </button>
        <div className={styles.headerRow}>
          <div className={styles.headerIdentity}>
            <Avatar name={name} size="lg" />
            <div className={styles.headerText}>
              <h1 className={styles.title}>{name}</h1>
              {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
            </div>
          </div>
          <div className={styles.headerActions}>
            <EmailAction
              hasEmail={Boolean(contact.email?.trim())}
              onOpen={() => setComposeOpen(true)}
            />
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => setCreatingTask(true)}
            >
              <Plus size={14} aria-hidden /> Nueva tarea
            </button>
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button type="button" className={styles.moreBtn} aria-label="Más acciones">
                  <MoreHorizontal size={16} aria-hidden />
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content className={styles.menuContent} align="end" sideOffset={6}>
                  <DropdownMenu.Item
                    className={styles.menuItem}
                    onSelect={() => setEditing((v) => !v)}
                  >
                    <Pencil size={14} aria-hidden />
                    {editing ? 'Cancelar edición' : 'Editar contacto'}
                  </DropdownMenu.Item>
                  {canDelete && (
                    <>
                      <DropdownMenu.Separator className={styles.menuSep} />
                      <DropdownMenu.Item
                        className={`${styles.menuItem} ${styles.menuItemDanger}`}
                        onSelect={() => {
                          void handleDelete();
                        }}
                        disabled={deleteMut.isPending}
                      >
                        <Trash2 size={14} aria-hidden />
                        Borrar contacto
                      </DropdownMenu.Item>
                    </>
                  )}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          </div>
        </div>
        {actionError && (
          <p className={styles.errorBanner} role="alert">
            {actionError}
          </p>
        )}
      </header>

      <div className={styles.columns}>
        <aside className={styles.rail}>
          <ContactIdentityCard contact={contact} editing={editing} onEditingChange={setEditing} />
          <ContactNotesCard key={contact.id} contact={contact} />
        </aside>

        <main className={styles.main}>
          <ActivitySection activities={activities} />

          <section className={styles.block}>
            <h2 className={styles.blockTitle}>
              Oportunidades
              <span className={styles.count}>{leads?.length ?? 0}</span>
            </h2>
            {!leads || leads.length === 0 ? (
              <p className={styles.emptyLine}>Sin oportunidades.</p>
            ) : (
              <ul className={styles.leadsList}>
                {leads.map((lead) => (
                  <li key={lead.id}>
                    <Link
                      to="/oportunidades/$leadId"
                      params={{ leadId: lead.id }}
                      className={styles.leadLink}
                    >
                      <span className={styles.leadMain}>
                        <span className={styles.leadTitle}>{lead.title || '(sin título)'}</span>
                        <span className={styles.leadMeta}>
                          {lead.expand?.stage?.name || '—'} · {formatDate(lead.created)}
                          {lead.value != null && (
                            <>
                              {' · '}
                              {formatMoney(lead.value, leadCurrency(lead, selectedTenant))}
                            </>
                          )}
                        </span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <ContactTasksCard
            contact={contact}
            contactName={name}
            leadOptions={leadOptions}
            creating={creatingTask}
            onCreatingChange={setCreatingTask}
          />

          <ContactConversations contactId={contactId} />
        </main>
      </div>

      <Dialog.Root
        open={composeOpen && Boolean(contact.email?.trim())}
        onOpenChange={setComposeOpen}
      >
        <Dialog.Portal>
          <Dialog.Overlay className={styles.dialogOverlay} />
          <Dialog.Content className={styles.dialogContent} aria-describedby={undefined}>
            <Dialog.Title className={styles.srOnly}>Enviar email</Dialog.Title>
            <Dialog.Close className={styles.dialogClose} aria-label="Cerrar">
              <XIcon size={16} />
            </Dialog.Close>
            <EmailComposer
              tenantId={contact.tenant}
              contactId={contact.id}
              contactEmail={contact.email}
              previewVars={{
                'contact.first_name': contact.first_name ?? '',
                'contact.last_name': contact.last_name ?? '',
                'contact.email': contact.email ?? '',
                'contact.company': contact.company ?? '',
                'contact.phone': contact.phone ?? '',
              }}
            />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}

function ActivitySection({
  activities,
}: {
  activities: ReturnType<typeof useContactActivities>['data'];
}) {
  const count = activities?.length ?? 0;
  const [openMail, setOpenMail] = useState<Activity | null>(null);
  return (
    <section className={count > 0 ? styles.activity : styles.block} aria-label="Actividad">
      <h2 className={styles.blockTitle}>
        Actividad
        <span className={styles.count}>{count}</span>
      </h2>
      {count === 0 ? (
        <p className={styles.emptyLine}>Sin actividad todavía.</p>
      ) : (
        <ol className={styles.timeline}>
          {activities!.map((act) => {
            const meta = ACTIVITY_META[act.type] ?? {
              icon: StickyNote,
              label: act.type,
              tone: 'neutral' as const,
            };
            const Icon = meta.icon;
            const isMail = act.type === 'email_sent';
            return (
              <li
                key={act.id}
                className={isMail ? `${styles.timelineItem} ${styles.timelineItemMail}` : styles.timelineItem}
                onClick={isMail ? () => setOpenMail(act) : undefined}
              >
                <span
                  className={`${styles.timelineIcon} ${styles[`tone_${meta.tone}`]}`}
                  aria-hidden
                >
                  <Icon size={14} />
                </span>
                <div className={styles.timelineBody}>
                  <div className={styles.timelineHead}>
                    <span className={styles.timelineType}>{meta.label}</span>
                    <time className={styles.timelineDate} dateTime={act.occurred_at}>
                      {formatDate(act.occurred_at)}
                    </time>
                  </div>
                  {act.subject && <p className={styles.timelineSubject}>{act.subject}</p>}
                  {isMail && <ViewEmailButton onClick={() => setOpenMail(act)} />}
                  {act.lead && (
                    <Link
                      to="/oportunidades/$leadId"
                      params={{ leadId: act.lead }}
                      className={styles.inlineLink}
                      onClick={(event) => event.stopPropagation()}
                    >
                      Ir a la oportunidad
                    </Link>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      )}
      <SentEmailDialog
        activity={openMail}
        open={openMail != null}
        onOpenChange={(next) => {
          if (!next) setOpenMail(null);
        }}
      />
    </section>
  );
}

function ContactIdentityCard({
  contact,
  editing,
  onEditingChange,
}: {
  contact: Contact;
  editing: boolean;
  onEditingChange: (next: boolean) => void;
}) {
  const update = useUpdateContact();
  const [form, setForm] = useState<Partial<Contact>>(() => identityForm(contact));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const tags = parseTags(contact.tags);
  const wasEditing = useRef(false);

  useEffect(() => {
    if (editing && !wasEditing.current) {
      setForm(identityForm(contact));
      setError(null);
    }
    wasEditing.current = editing;
  }, [editing, contact]);

  function cancel() {
    setForm(identityForm(contact));
    setError(null);
    onEditingChange(false);
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSaved(false);
    try {
      await update.mutateAsync({ contactId: contact.id, patch: form });
      onEditingChange(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al guardar.');
    }
  }

  return (
    <section className={styles.block} id="contact-identity" aria-label="Identidad">
      <h2 className={styles.blockTitle}>Identidad</h2>

      {!editing ? (
        <dl className={styles.identityList}>
          <IdentityRow icon={Mail} label="Email">
            {contact.email ? (
              <a href={`mailto:${contact.email}`} className={styles.inlineLink}>
                {contact.email}
              </a>
            ) : (
              <span className={styles.muted}>—</span>
            )}
          </IdentityRow>
          <IdentityRow icon={Phone} label="Teléfono">
            {contact.phone ? (
              <a
                href={phoneToTelLink(contact.phone) ?? `tel:${contact.phone}`}
                className={styles.inlineLink}
              >
                {prettyPhone(contact.phone)}
              </a>
            ) : (
              <span className={styles.muted}>—</span>
            )}
          </IdentityRow>
          <IdentityRow icon={Building2} label="Empresa">
            {contact.company ? (
              <span>{contact.company}</span>
            ) : (
              <span className={styles.muted}>—</span>
            )}
          </IdentityRow>
          {contact.position && (
            <IdentityRow label="Cargo">
              <span>{contact.position}</span>
            </IdentityRow>
          )}
          {tags.length > 0 && (
            <div className={styles.tagsRow}>
              <span className={styles.fieldLabel}>Tags</span>
              <TagList tags={tags} />
            </div>
          )}
        </dl>
      ) : (
        <form className={styles.editForm} onSubmit={handleSave}>
          <Field label="Email">
            <input
              type="email"
              className={styles.input}
              value={form.email ?? ''}
              autoFocus
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            />
          </Field>
          <Field label="Teléfono">
            <input
              type="tel"
              className={styles.input}
              value={form.phone ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            />
          </Field>
          <Field label="Nombre">
            <input
              type="text"
              className={styles.input}
              value={form.first_name ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, first_name: e.target.value }))}
            />
          </Field>
          <Field label="Apellido">
            <input
              type="text"
              className={styles.input}
              value={form.last_name ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, last_name: e.target.value }))}
            />
          </Field>
          <Field label="Empresa">
            <input
              type="text"
              className={styles.input}
              value={form.company ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
            />
          </Field>
          <Field label="Cargo">
            <input
              type="text"
              className={styles.input}
              value={form.position ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, position: e.target.value }))}
            />
          </Field>

          {error && (
            <p className={styles.errorBanner} role="alert">
              {error}
            </p>
          )}

          <div className={styles.formActions}>
            <button type="button" className="btn btn--ghost btn--sm" onClick={cancel}>
              Cancelar
            </button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={update.isPending}>
              {update.isPending ? (
                <>
                  <Loader2 size={14} aria-hidden className={styles.spinIcon} />
                  Guardando…
                </>
              ) : (
                <>
                  <Save size={14} aria-hidden /> Guardar
                </>
              )}
            </button>
          </div>
        </form>
      )}

      {saved && !editing && (
        <p className={styles.savedBadge}>
          <CheckCircle2 size={12} aria-hidden /> Guardado
        </p>
      )}
    </section>
  );
}

function identityForm(contact: Contact): Partial<Contact> {
  return {
    email: contact.email,
    phone: contact.phone,
    first_name: contact.first_name,
    last_name: contact.last_name,
    company: contact.company,
    position: contact.position,
  };
}

function IdentityRow({
  icon: Icon,
  label,
  children,
}: {
  icon?: LucideIcon;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className={styles.identityRow}>
      <dt>
        {Icon && <Icon size={12} aria-hidden />} {label}
      </dt>
      <dd>{children}</dd>
    </div>
  );
}

function ContactNotesCard({ contact }: { contact: Contact }) {
  const update = useUpdateContact();
  const [notes, setNotes] = useState(contact.notes ?? '');
  const [saved, setSaved] = useState(false);
  const dirty = notes !== (contact.notes ?? '');

  async function handleSave() {
    setSaved(false);
    await update.mutateAsync({ contactId: contact.id, patch: { notes } });
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  }

  return (
    <section className={styles.block} aria-label="Notas">
      <h2 className={styles.blockTitle}>Notas</h2>
      <textarea
        className={styles.textarea}
        rows={3}
        value={notes}
        onChange={(e) => {
          setNotes(e.target.value);
          setSaved(false);
        }}
        placeholder="Notas privadas sobre este contacto, contexto de la relación, etc."
      />
      <div className={styles.notesFooter}>
        {saved && !dirty && (
          <span className={styles.savedBadge}>
            <CheckCircle2 size={12} aria-hidden /> Guardado
          </span>
        )}
        <button
          type="button"
          className={`btn btn--primary btn--sm ${styles.notesSave}`}
          onClick={handleSave}
          disabled={!dirty || update.isPending}
        >
          {update.isPending ? (
            <>
              <Loader2 size={14} aria-hidden className={styles.spinIcon} />
              Guardando…
            </>
          ) : (
            <>
              <Save size={14} aria-hidden /> Guardar notas
            </>
          )}
        </button>
      </div>
    </section>
  );
}

function ContactTasksCard({
  contact,
  contactName,
  leadOptions,
  creating,
  onCreatingChange,
}: {
  contact: Contact;
  contactName: string;
  leadOptions: Array<{ id: string; title: string }>;
  creating: boolean;
  onCreatingChange: (next: boolean) => void;
}) {
  const { data: tasks = [] } = useContactTasks(contact.id);
  const toggle = useToggleTaskDone();
  const del = useDeleteTask();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Task | null>(null);

  const open = tasks.filter((t) => t.status === 'open' || t.status === 'in_progress');
  const done = tasks.filter((t) => t.status === 'done' || t.status === 'cancelled');

  async function handleDelete(task: Task) {
    const ok = await confirm({
      title: 'Eliminar tarea',
      description: `Vas a eliminar "${task.title}". La acción no se puede deshacer.`,
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (ok) del.mutate(task.id);
  }

  return (
    <section className={styles.block} aria-label="Tareas">
      <h2 className={styles.blockTitle}>
        Tareas pendientes
        <span className={styles.count}>{open.length}</span>
      </h2>

      {open.length === 0 ? (
        <p className={styles.emptyLine}>Sin tareas pendientes.</p>
      ) : (
        <TaskList
          tasks={open}
          onToggle={(task, isDone) => toggle.mutate({ task, done: !isDone })}
          onEdit={setEditing}
          onDelete={handleDelete}
        />
      )}

      {done.length > 0 && (
        <details className={styles.doneDetails}>
          <summary>Completadas ({done.length})</summary>
          <TaskList
            tasks={done}
            done
            onToggle={(task, isDone) => toggle.mutate({ task, done: !isDone })}
            onEdit={setEditing}
            onDelete={handleDelete}
          />
        </details>
      )}

      {creating && (
        <TaskFormModal
          onClose={() => onCreatingChange(false)}
          preset={{
            contactId: contact.id,
            contactLabel: contactName,
            leadOptions,
          }}
        />
      )}
      {editing && (
        <TaskFormModal
          task={editing}
          onClose={() => setEditing(null)}
          preset={{
            contactId: contact.id,
            contactLabel: contactName,
            leadOptions,
          }}
        />
      )}
    </section>
  );
}

function TaskList({
  tasks,
  done = false,
  onToggle,
  onEdit,
  onDelete,
}: {
  tasks: Task[];
  done?: boolean;
  onToggle: (task: Task, isDone: boolean) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
}) {
  return (
    <ul className={styles.taskList}>
      {tasks.map((task) => {
        const isDone = done || task.status === 'done' || task.status === 'cancelled';
        const taskType = task.type || 'todo';
        return (
          <li key={task.id} className={isDone ? `${styles.task} ${styles.taskDone}` : styles.task}>
            <button
              type="button"
              className={styles.taskCheck}
              onClick={() => onToggle(task, isDone)}
              aria-label={isDone ? 'Reabrir' : 'Completar'}
            >
              {isDone ? <CheckCircle2 size={16} /> : <span />}
            </button>
            <div className={styles.taskBody}>
              <button type="button" className={styles.taskTitle} onClick={() => onEdit(task)}>
                {task.title}
              </button>
              <div className={styles.taskMeta}>
                {taskType !== 'todo' && <span>{TASK_TYPE_LABEL[taskType]}</span>}
                {task.due_date && <span>{task.due_date.slice(0, 10)}</span>}
                {task.lead && task.expand?.lead && (
                  <Link
                    to="/oportunidades/$leadId"
                    params={{ leadId: task.lead }}
                    className={styles.inlineLink}
                  >
                    {task.expand.lead.title || 'Oportunidad'}
                  </Link>
                )}
              </div>
            </div>
            <button
              type="button"
              className={styles.taskDel}
              onClick={() => onDelete(task)}
              aria-label="Eliminar"
            >
              <Trash2 size={12} />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className={styles.field}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
    </label>
  );
}
